import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatListModule } from '@angular/material/list';
import _ from 'lodash';
import { Observable, filter, from, of, switchMap, tap } from 'rxjs';
import { BazisMaterialHelper } from '../../../helpers/bazis-material.helper';
import { getFullCountertopThickness } from '../../../helpers/countertop.utils';
import { Countertop, CountertopMaterial } from '../../../models/cutting/countertop';
import { CountertopEdgeBanding } from '../../../models/cutting/countertop/outline';
import { CuttingButt } from '../../../models/cutting/cutting-butt.model';
import { AngleMode } from '../../../models/cutting/cutting-panel-outline.model';
import { CuttingButtFormService } from '../../../services/cutting-butt-form.service';
import { LegacyDialogsService } from '../../../services/legacy-dialogs.service';
import {
  EdgeInColorDialogData,
  EdgesInColorDialogComponent,
} from '../../edges-in-color-dialog/edges-in-color-dialog.component';
import {
  MaterialBaseButtsDialogComponent,
  MaterialBaseEdgeDialogData,
} from '../../material-base-butts-dialog/material-base-butts-dialog.component';
import { CountertopSettingsStateService } from '../countertop-settings-state.service';
import {
  EdgeBandingWizardDialogComponent,
  EdgeBandingWizardDialogData,
  EdgeBandingWizardDialogResult,
} from '../edge-banding-wizard-dialog/edge-banding-wizard-dialog.component';
import { ItemCardComponent } from '../item-card/item-card.component';

export class EdgeBandingBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _EdgeBandingMixinBase = mixinDisabled(EdgeBandingBase);

export interface EdgeBandingView {
  sideNumber: number;
  postforming: boolean;
  isActive: boolean;
  value: CountertopEdgeBanding;
}

@Component({
  selector: 'csl-edge-banding',
  templateUrl: 'edge-banding.component.html',
  standalone: true,
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
  imports: [
    NgIf,
    AsyncPipe,
    ItemCardComponent,
    NgFor,
    MatLegacyButtonModule,
    EdgeBandingWizardDialogComponent,
    MatListModule,
    MatIconModule,
    NgClass,
    MatTooltipModule,
  ],
  providers: [CuttingButtFormService],
})
export class EdgeBandingComponent extends _EdgeBandingMixinBase implements CanDisable {
  @Input() showQuantityInStock = false;

  @Output() selectionChange: EventEmitter<{ sideNumber: number; edgeBanding: CountertopEdgeBanding }[]> =
    new EventEmitter();

  data$: Observable<any>;

  countertop: Countertop;
  edgeBanding: EdgeBandingView[];

  action$: Observable<unknown>;

  constructor(
    private countertopSettingsService: CountertopSettingsStateService,
    private dialogs: LegacyDialogsService,
    private cuttingButtFormService: CuttingButtFormService
  ) {
    super();

    this.data$ = this.countertopSettingsService.countertop$.pipe(
      tap((p: Countertop) => {
        this.countertop = p;

        this.edgeBanding = Object.entries(this.countertop?.outline?.edgeBanding)
          .filter(([_, value]) => !!value)
          .map(([key, value]) => ({
            sideNumber: +key,
            postforming: this.countertop?.checkSidePostforming(key),
            value: value,
            isActive: false,
          }));
      })
    );
  }

  deleteEdgeBanding(item: EdgeBandingView): void {
    this.countertop.outline.edgeBanding[item.sideNumber] = undefined;
    this.countertopSettingsService.updateCountertop(this.countertop);
  }

  applyEdgeBandingToAllSides(): void {
    this.action$ = this.selectBanding().pipe(
      filter(res => !!res),
      tap((res: CuttingButt) => {
        Object.keys(this.countertop.outline.edgeBanding)
          .filter(sn => !this.countertop.checkSidePostforming(sn))
          .forEach(sn => {
            this.countertop.outline.edgeBanding[sn] = { ...res, sideNumber: +sn };
          });

        Object.keys(this.countertop.outline.angleProcessing).forEach(an => {
          if (
            this.countertop.outline.angleProcessing[an] &&
            this.countertop.outline.angleProcessing[an]?.mode !== AngleMode.None
          ) {
            this.countertop.outline.angleProcessing[an].parameters.edgeBanding = { ...res };
          }
        });

        this.countertopSettingsService.updateCountertop(this.countertop);
      })
    );
  }

  openMaterialBaseDialog(): Observable<CuttingButt | undefined> {
    return this.dialogs.component(
      MaterialBaseButtsDialogComponent,
      {
        panelThickness: getFullCountertopThickness(this.countertop),
      } as MaterialBaseEdgeDialogData,
      1000
    );
  }

  getDescription(item: EdgeBandingView): string {
    if (item.postforming) {
      return $localize`Постформинг`;
    }

    return item.value ? BazisMaterialHelper.formatMaterialNameAndArticle(item.value?.name, item.value?.article) : '';
  }

  selectBanding(): Observable<CuttingButt> {
    return (
      this.countertop.material
        ? from(
            this.cuttingButtFormService.getMatchedButtsForClient(
              this.countertop.material.inMaterialBaseId,
              this.countertop.material.thickness
            )
          )
        : of(null)
    ).pipe(
      switchMap(edges => {
        if (edges?.length > 1) {
          return this.getEdgeBandingInColor(this.countertop.material);
        } else {
          return this.openMaterialBaseDialog();
        }
      })
    );
  }

  addBanding(): void {
    this.action$ = this.dialogs
      .component<EdgeBandingWizardDialogData, EdgeBandingWizardDialogResult>(EdgeBandingWizardDialogComponent, {
        countertop: this.countertop,
        showQuantityInStock: this.showQuantityInStock,
        countertopThickness: getFullCountertopThickness(this.countertop),
        materialInMatBaseId: this.countertop.material.inMaterialBaseId,
      })
      .pipe(
        filter(e => !_.isNil(e)),
        tap((e: EdgeBandingWizardDialogResult) => {
          e.sideNumbers.forEach(n => (this.countertop.outline.edgeBanding[n] = { ...e.banding, sideNumber: +n }));

          this.countertopSettingsService.updateCountertop(this.countertop);
        })
      );
  }

  private getEdgeBandingInColor(material: CountertopMaterial): Observable<CuttingButt> {
    return this.dialogs.component<EdgeInColorDialogData, CuttingButt>(
      EdgesInColorDialogComponent,
      {
        panelThickness: material.thickness,
        materialInMatBaseId: material.inMaterialBaseId,
        showQuantityInStock: this.showQuantityInStock,
      },
      1000
    );
  }

  toggleSelection(item: EdgeBandingView): void {
    this.edgeBanding.forEach(b => {
      if (item !== b) {
        b.isActive = false;
      }
    });

    item.isActive = !item.isActive;

    this.selectionChange.emit(
      this.edgeBanding.filter(b => b.isActive).map(b => ({ sideNumber: +b.sideNumber, edgeBanding: b.value }))
    );
  }
}
